/**
 * Hook để track impression khi item xuất hiện trong viewport
 *
 * Behavior:
 * - Navigate đi và quay lại: log lại từ đầu
 * - Chuyển tab và quay lại: log lại từ đầu
 * - Scroll lên/xuống trong cùng session: không log lại
 */

import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { trackEvent } from '@/lib/event-tracking';

interface ImpressionTrackingOptions {
  itemId: string | number;
  eventParams: Record<string, string | number | boolean>;
  buttonName?: string;
  buttonLocation: string;
  enabled?: boolean;
}

// Session ID để đảm bảo reset hoàn toàn khi navigate/switch tab
let sessionId = 0;

// Cache đã track trong session hiện tại
const impressionCache = new Set<string>();

// Navigation counter để force reset ngay cả khi pathname giống nhau
let navigationCount = 0;

// Store metadata cho mỗi element để track
interface ElementMetadata {
  itemId: string | number;
  buttonLocation: string;
  pathname: string;
  eventParams: Record<string, string | number | boolean>;
}

// Shared IntersectionObserver
let sharedObserver: IntersectionObserver | null = null;
const elementMetadata = new Map<Element, ElementMetadata>();

function getSharedObserver(): IntersectionObserver {
  if (sharedObserver) return sharedObserver;

  sharedObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const metadata = elementMetadata.get(entry.target);
          if (!metadata) return;

          const trackingKey = `${sessionId}_${metadata.pathname}_${metadata.buttonLocation}_${metadata.itemId}`;

          if (!impressionCache.has(trackingKey)) {
            impressionCache.add(trackingKey);
            trackEvent('solomeal_event', {
              event_type: 'impression',
              action_time: Date.now(),
              ...metadata.eventParams,
            });
          }
        }
      });
    },
    { threshold: 0.8, rootMargin: '0px' }
  );

  return sharedObserver;
}

function forceRecheckAll() {
  if (!sharedObserver || elementMetadata.size === 0) return;

  sharedObserver.disconnect();

  elementMetadata.forEach((_, element) => {
    sharedObserver!.observe(element);
  });
}

export function useItemImpression({
  itemId,
  eventParams,
  buttonLocation,
  enabled = true,
}: ImpressionTrackingOptions) {
  const elementRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  useEffect(() => {
    if (!enabled || !elementRef.current) return;

    const element = elementRef.current;
    const observer = getSharedObserver();

    elementMetadata.set(element, {
      itemId,
      buttonLocation,
      pathname,
      eventParams,
    });

    observer.observe(element);

    return () => {
      elementMetadata.delete(element);
      observer.unobserve(element);
    };
  }, [itemId, buttonLocation, pathname, enabled, eventParams]);

  return elementRef;
}

/**
 * Hook để reset tracking khi navigate hoặc chuyển tab
 * ĐẶT Ở COMPONENT CHA CHỨA DANH SÁCH ITEMS
 */
export function useImpressionTrackingReset() {
  const pathname = usePathname();
  const navigationCountRef = useRef(navigationCount);

  useEffect(() => {
    navigationCount++;

    if (navigationCountRef.current !== navigationCount) {
      sessionId++;
      impressionCache.clear();
      navigationCountRef.current = navigationCount;

      const timer = setTimeout(() => {
        forceRecheckAll();
      }, 250);

      return () => clearTimeout(timer);
    }

    navigationCountRef.current = navigationCount;
  }, [pathname]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        sessionId++;
        impressionCache.clear();
        setTimeout(() => forceRecheckAll(), 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
}
